import { useCallback, useEffect, useState } from "react";
import { fetchSettings, updateSetting, getSettingValue, fetchImportLogs } from "../tipsApi";
import type { ImportLog } from "../types";
import { PosImport } from "../components/PosImport";

function fmtTimestamp(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function TipsSettingsView() {
  const [feePct, setFeePct] = useState("");
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([fetchSettings(), fetchImportLogs()]);
      setFeePct(getSettingValue(s, "processing_fee_pct", "5.0"));
      setLogs(l);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSaveFee() {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await updateSetting("processing_fee_pct", feePct);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6">
      {error && (
        <div className="rounded-lg bg-red-900/20 px-3 py-2 text-sm text-red-400">{error}</div>
      )}

      {/* Processing fee */}
      <section>
        <h2 className="mb-3 text-lg font-semibold">Processing Fee</h2>
        <div className="flex items-end gap-3">
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">Fee Percentage (%)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={feePct}
              onChange={(e) => setFeePct(e.target.value)}
              className="w-28 rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm tabular-nums focus:border-tribal-red focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={handleSaveFee}
            disabled={saving}
            className="rounded-md bg-tribal-red px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80 disabled:opacity-50"
          >
            {saving ? "Saving…" : saved ? "Saved" : "Save"}
          </button>
        </div>
        <p className="mt-2 text-xs text-tribal-muted">
          Deducted from the raw tip pool before distributing to bartenders.
        </p>
      </section>

      {/* POS Import */}
      <section>
        <h2 className="mb-3 text-lg font-semibold">Import</h2>
        <PosImport />
      </section>

      {/* Import log */}
      {logs.length > 0 && (
        <section>
          <h2 className="mb-3 text-lg font-semibold">Import History</h2>
          <div className="overflow-x-auto rounded-lg border border-tribal-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-tribal-border bg-tribal-surface text-left text-tribal-muted">
                  <th className="px-3 py-2 font-medium">File</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 text-right font-medium">Rows</th>
                  <th className="px-3 py-2 text-right font-medium">Imported</th>
                  <th className="px-3 py-2 text-right font-medium">Skipped</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-tribal-border last:border-0">
                    <td className="max-w-[12rem] truncate px-3 py-1.5">{l.filename}</td>
                    <td className="px-3 py-1.5">{l.import_type === "pos_csv" ? "POS CSV" : "Excel"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{l.row_count ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{l.imported_count ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{l.skipped_count ?? "—"}</td>
                    <td className="px-3 py-1.5 text-tribal-muted">{fmtTimestamp(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
